export const colors = {
  brand: '#008578',
  brandDark: '#006b5f',
  border: '#e5e7eb',
  panel: '#f8f9fa',
  successBg: '#d1fae5',
  successText: '#065f46',
  info: '#e0f2fe',
  warning: '#fef3c7',
};

export const cardStyles = {
  rounded: { borderRadius: 3 },
  section: { mb: 3, borderRadius: 3 },
};

export const buttonStyles = {
  primary: {
    bgcolor: colors.brand,
    '&:hover': { bgcolor: colors.brandDark },
  },
};

export const chipStyles = {
  selected: { bgcolor: colors.successBg, color: colors.successText },
};

export const layoutStyles = {
  pageShell: { py: 5 },
  stickyAside: { position: { md: 'sticky' }, top: { md: 16 }, alignSelf: 'flex-start' },
};

export const topBarStyles = {
  shell: { bgcolor: '#fff', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  content: { py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 },
  logo: { display: 'block', width: { xs: 142, sm: 170 }, maxHeight: 48, objectFit: 'contain' },
};

export const heroStyles = {
  shell: { background: `linear-gradient(135deg, ${colors.brand} 0%, ${colors.brandDark} 100%)`, color: '#fff', py: { xs: 3.5, md: 6 } },
  chips: { mb: 2 },
  categoryChip: { bgcolor: '#fff', color: '#1f2937', textTransform: 'capitalize' },
  title: { fontWeight: 700, mb: 1, fontSize: { xs: '1.9rem', md: '3rem' } },
  subtitle: { opacity: 0.95, fontSize: { xs: '1rem', md: '1.25rem' } },
};

export const eventListStyles = {
  page: { minHeight: '100vh', bgcolor: '#f6faf9' },
  hero: { ...heroStyles.shell, py: { xs: 4, md: 7 } },
  heroTitle: { ...heroStyles.title, maxWidth: 760 },
  heroSubtitle: { ...heroStyles.subtitle, maxWidth: 720 },
  content: { py: { xs: 4, md: 5 } },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'center' },
    flexDirection: { xs: 'column', sm: 'row' },
    gap: 1.5,
    mb: 3,
  },
  eventCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 3,
    overflow: 'hidden',
    textDecoration: 'none',
    color: 'inherit',
    boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
    border: `1px solid ${colors.border}`,
    transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      borderColor: colors.brand,
      boxShadow: '0 16px 36px rgba(15, 23, 42, 0.14)',
    },
  },
  cardImage: { width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' },
  cardImageFallback: { width: '100%', aspectRatio: '16 / 9', display: 'grid', placeItems: 'center', bgcolor: '#e9f5f3', color: colors.brandDark },
  cardContent: { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 1.5 },
  categoryChip: { alignSelf: 'flex-start', bgcolor: '#e9f5f3', color: colors.brandDark, textTransform: 'capitalize', fontWeight: 700 },
  title: { fontWeight: 800, lineHeight: 1.2 },
  summary: { color: 'text.secondary', flexGrow: 1 },
  metaRow: { pt: 1.25, borderTop: `1px solid ${colors.border}` },
  metaIcon: { color: colors.brand, fontSize: 18, flexShrink: 0 },
  detailsButton: { ...buttonStyles.primary, mt: 0.5, alignSelf: 'flex-start' },
};

export const metaStripStyles = {
  shell: { mt: { xs: -2, md: -3 }, position: 'relative', zIndex: 1 },
  card: { borderRadius: 3, boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12)' },
  icon: { color: colors.brand },
  value: { fontWeight: 700 },
};

export const eventImageStyles = {
  image: {
    width: '100%',
    aspectRatio: '16 / 9',
    objectFit: 'contain',
    display: 'block',
    borderRadius: 3,
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    mb: 3,
  },
  fallback: {
    width: '100%',
    aspectRatio: '16 / 9',
    display: 'grid',
    placeItems: 'center',
    borderRadius: 3,
    bgcolor: '#e9f5f3',
    color: colors.brandDark,
    mb: 3,
  },
};

export const venueStyles = {
  panel: { bgcolor: colors.panel, p: 2, borderRadius: 2, mb: 2 },
  mapHeader: { mb: 1 },
  map: { width: '100%', height: { xs: 220, md: 300 }, border: 0, borderRadius: 2 },
};

export const mobileBarStyles = {
  shell: {
    display: { xs: 'block', md: 'none' },
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    bgcolor: '#fff',
    borderTop: `1px solid ${colors.border}`,
    px: 2,
    py: 1.5,
    boxShadow: '0 -6px 20px rgba(15, 23, 42, 0.12)',
  },
  content: { minWidth: 0, flex: 1 },
  detailText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  amount: { color: colors.brandDark, fontWeight: 900, fontSize: '1.05rem', lineHeight: 1.25 },
  button: { ...buttonStyles.primary, flexShrink: 0, whiteSpace: 'nowrap', alignSelf: 'center' },
};

export const registrationDialogStyles = {
  contentStack: { pt: 1 },
  summaryPanel: { bgcolor: colors.panel, borderRadius: 2, p: 2 },
  reviewPanel: { border: `1px solid ${colors.border}`, borderRadius: 2, p: 2 },
  attendeeAccordion: {
    border: `1px solid ${colors.border}`,
    borderRadius: '8px !important',
    boxShadow: 'none',
    overflow: 'hidden',
    '&:before': { display: 'none' },
  },
  attendeeAccordionSummary: {
    px: 1.25,
    minHeight: 52,
    '& .MuiAccordionSummary-content': {
      my: 1,
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      mr: 1,
    },
  },
  attendeeAccordionDetails: { px: 1.25, pb: 1.5, pt: 0 },
  couponGroup: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  couponField: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  couponButton: {
    ml: '-1px',
    height: 56,
    minWidth: 104,
    px: 2,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  paymentTitle: { mb: 1, fontWeight: 800 },
  paymentOption: (selected) => ({
    display: 'block',
    height: '100%',
    border: '2px solid',
    borderColor: selected ? colors.brand : colors.border,
    borderRadius: 2,
    p: 2,
    minHeight: 150,
    cursor: 'pointer',
    bgcolor: selected ? 'rgba(0, 133, 120, 0.07)' : '#fff',
    boxShadow: selected ? '0 10px 24px rgba(0, 97, 88, 0.14)' : 'none',
    transition: 'border-color .2s ease, box-shadow .2s ease, background-color .2s ease',
    '&:hover': {
      borderColor: colors.brand,
      boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
    },
  }),
  paymentInput: { position: 'absolute', opacity: 0, pointerEvents: 'none' },
  paymentLogoGroup: {
    mt: 'auto',
    pt: 1,
    minHeight: 38,
    alignItems: 'center',
    overflow: 'hidden',
  },
  paymentLogoTile: {
    width: 54,
    height: 34,
    display: 'grid',
    placeItems: 'center',
    p: 0,
    flexShrink: 0,
    overflow: 'hidden',
  },
  paymentLogo: {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'contain',
    objectPosition: 'center',
  },
  paymentCheck: { color: colors.brand, fontSize: 22 },
  paymentBadge: (selected) => ({
    alignSelf: 'flex-start',
    borderRadius: 999,
    px: 1.25,
    py: 0.4,
    fontSize: '0.72rem',
    fontWeight: 900,
    letterSpacing: 0,
    color: selected ? '#fff' : colors.brandDark,
    bgcolor: selected ? colors.brand : colors.panel,
    border: `1px solid ${selected ? colors.brand : colors.border}`,
  }),
  paymentOptionTitle: { fontWeight: 900, color: colors.brandDark, lineHeight: 1.25 },
  dialogTitle: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    pr: 6,
  },
  titleText: {
    flex: 1,
    minWidth: 0,
  },
  titleBackButton: {
    ml: -1,
  },
  titleCloseButton: {
    position: 'absolute',
    right: 8,
    top: 8,
  },
  actions: { px: 3, pb: 2 },
  actionsWithTotal: {
    px: 3,
    pt: 1.75,
    pb: 2,
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 1.5,
    borderTop: `1px solid ${colors.border}`,
    bgcolor: colors.panel,
    '& > :not(style)': { ml: 0 },
  },
  submitGroup: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 0.75,
    '& .MuiButton-root': { alignSelf: 'flex-end' },
  },
  termsNotice: {
    width: '100%',
    color: 'text.disabled',
    fontSize: '0.75rem',
    lineHeight: 1.45,
    textAlign: { xs: 'left', sm: 'right' },
  },
  termsLink: {
    color: 'inherit',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    fontWeight: 500,
    '&:hover': {
      color: 'text.secondary',
    },
  },
  amountSummary: {
    width: '100%',
  },
  amountRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 2,
  },
  amountDiscount: {
    fontWeight: 700,
    fontSize: '1rem',
    mb: 0.25,
  },
  amountTotalLabel: {
    fontWeight: 800,
    fontSize: '1.15rem',
    color: colors.brandDark,
  },
  amountTotalValue: {
    fontWeight: 900,
    fontSize: { xs: '1.6rem', sm: '1.85rem' },
    lineHeight: 1.15,
    color: colors.brandDark,
    letterSpacing: '-0.02em',
  },
  primaryButton: buttonStyles.primary,
};

export const paymentOutcomeStyles = {
  loading: { color: colors.brand, bg: '#e9f5f3', border: '#b7ded8' },
  success: { color: colors.successText, bg: '#ecfdf5', border: '#a7f3d0' },
  error: { color: '#991b1b', bg: '#fef2f2', border: '#fecaca' },
  info: { color: colors.brandDark, bg: '#f8fafc', border: colors.border },
};

export const paymentStatusStyles = {
  page: { minHeight: '100vh', bgcolor: '#f6faf9' },
  container: { py: { xs: 4, md: 7 } },
  card: { borderRadius: 3, boxShadow: '0 16px 40px rgba(15, 23, 42, 0.12)', border: `1px solid ${colors.border}` },
  cardContent: { p: { xs: 3, sm: 4, md: 5 } },
  contentStack: { alignItems: 'center', textAlign: 'center' },
  iconWrap: (outcomeStyle) => ({ color: outcomeStyle.color, display: 'grid', placeItems: 'center' }),
  statusMark: { width: 64, height: 64, borderRadius: '50%', border: '3px solid currentColor', display: 'grid', placeItems: 'center', fontSize: 38, fontWeight: 900 },
  headline: { maxWidth: 620 },
  title: (outcomeStyle) => ({ fontWeight: 900, mb: 1, color: outcomeStyle.color }),
  amountPanel: (outcomeStyle) => ({ width: '100%', bgcolor: outcomeStyle.bg, borderRadius: 3, p: 2.5, border: `1px solid ${outcomeStyle.border}` }),
  amountLabel: { textTransform: 'uppercase', fontWeight: 800 },
  amount: (outcomeStyle) => ({ fontWeight: 900, color: outcomeStyle.color }),
  errorAlert: { width: '100%', textAlign: 'center', justifyContent: 'center' },
  detailsPanel: { width: '100%', bgcolor: '#fff', border: `1px solid ${colors.border}`, borderRadius: 2, p: { xs: 2.5, md: 3 } },
  detailsHeader: { mb: 1.5 },
  detailsTitle: { fontWeight: 900 },
  detailRow: { width: '100%', py: 1, borderBottom: `1px solid ${colors.border}` },
  detailLabel: { textAlign: { xs: 'center', sm: 'left' } },
  detailValue: { fontWeight: 800, textAlign: { xs: 'center', sm: 'right' }, overflowWrap: 'anywhere' },
  ticketPanel: { width: '100%', bgcolor: '#fff', border: `1px solid ${colors.border}`, borderRadius: 2, p: { xs: 2.5, md: 3 } },
  primaryActionButton: { ...buttonStyles.primary, px: 3 },
  secondaryActionButton: { px: 3, borderColor: colors.brand, color: colors.brandDark },
  paymentActions: { width: '100%', justifyContent: 'center' },
  actions: { width: '100%', pt: 0.5, alignItems: 'center', textAlign: 'center' },
  actionButton: { color: 'text.secondary' },
};

export const registrationStyles = {
  sectionCard: { borderRadius: 3 },
  headerIcon: { color: colors.brand },
  intro: { mb: 2 },
  warning: { mb: 2 },
  sessionCard: (selected, available) => ({
    border: '2px solid',
    borderColor: selected ? colors.brand : '#e9ecef',
    borderRadius: 2,
    p: 2,
    cursor: available ? 'pointer' : 'not-allowed',
    bgcolor: selected ? 'rgba(0, 133, 120, 0.05)' : '#fff',
    opacity: available ? 1 : 0.62,
    transition: 'all .2s ease',
    '&:focus-visible': {
      outline: '3px solid rgba(0, 133, 120, 0.28)',
      outlineOffset: 2,
    },
  }),
  ticketPreviewChip: (available) => ({ bgcolor: available ? colors.brand : colors.border, color: available ? '#fff' : '#6b7280' }),
  selectedArea: { mt: 2, pt: 2, borderTop: `1px solid ${colors.border}` },
  ticketCard: (selected, available) => ({
    border: '2px solid',
    borderColor: selected ? colors.brand : colors.border,
    borderRadius: 2,
    p: 1.5,
    height: '100%',
    bgcolor: selected ? 'rgba(0, 133, 120, 0.06)' : '#fff',
    cursor: available ? 'pointer' : 'not-allowed',
    opacity: available ? 1 : 0.6,
    '&:focus-visible': {
      outline: '3px solid rgba(0, 133, 120, 0.28)',
      outlineOffset: 2,
    },
  }),
  ticketQuantity: { pt: 1, borderTop: '1px solid #dce6e4' },
  summaryPanel: { mt: 2, bgcolor: colors.warning, borderRadius: 2, p: 2, border: `1px solid ${colors.border}` },
  summaryInfoGroup: { display: 'grid', gap: 1.25 },
  summaryActionRow: { display: 'flex', justifyContent: 'center' },
  confirmButton: { ...buttonStyles.primary, py: 1.25, whiteSpace: 'nowrap' },
};

export const numberSpinnerStyles = {
  group: (error, disabled) => ({
    display: 'grid',
    gridTemplateColumns: '36px minmax(48px, 1fr) 36px',
    alignItems: 'center',
    width: '100%',
    maxWidth: 170,
    border: '1px solid',
    borderColor: error ? 'error.main' : '#cbd5e1',
    borderRadius: 2,
    bgcolor: disabled ? '#f8fafc' : '#fff',
    overflow: 'hidden',
  }),
  button: { minWidth: 0, width: 36, height: 36, borderRadius: 0, color: colors.brandDark },
  input: {
    width: '100%',
    height: 36,
    border: 0,
    borderLeft: '1px solid #e2e8f0',
    borderRight: '1px solid #e2e8f0',
    textAlign: 'center',
    font: 'inherit',
    fontWeight: 800,
    bgcolor: 'transparent',
    outline: 0,
    '&:disabled': { color: 'text.disabled' },
  },
};
